// Chi tiết thẻ liệu trình (drawer): số dư, dịch vụ, thanh toán theo đợt, sử dụng theo tháng, lịch hẹn, lịch sử giao dịch
import React, { useMemo, useState } from 'react';
import { Drawer, Dropdown, Empty, Input, Modal, Table, Tabs, Timeline, Tooltip, message } from 'antd';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  CalendarClock, CalendarPlus, CircleDollarSign, FileText, PencilRuler, Printer, ReceiptText, ScanLine, SlidersHorizontal, Undo2, X,
} from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import { METHOD_LABEL, branchName, staffName } from '../POS/data';
import { formatPhone } from '../POS/utils';
import { KIND_LABEL, ROLE_LABEL, SOURCE_LABEL, serviceOf, type CardBooking, type CardEventKind, type CardUsage } from './data';
import {
  awaitingActivation, canCancelUsage, cardBalance, cardFlags, consumedAmount, installmentRows, lineBalances, monthlyUsage, noExpiry, outstanding, paidAmount,
} from './logic';
import useTreatmentStore from './store';
import { useTreatmentAuth } from './actions';
import { AdjustModal, BookingModal, DeductModal, ExtendModal, PlanModal, RefundModal } from './modals';
import PayRequestModal, { pendingRequestOf } from './PayRequestModal';
import { BalanceCell, CARD_STATUS, FlagPills, INSTALLMENT_STATE, Pill, fmtDate, fmtDateTime, money } from './ui';
import { printCardReceipt, printCardStatement } from './print';

type ModalKey = 'pay' | 'deduct' | 'booking' | 'extend' | 'adjust' | 'refund' | 'plan';

const EVENT_COLOR: Record<CardEventKind, string> = {
  issue: 'blue', payment: 'green', use: '#3e91ab', cancel_use: 'gray', extend: 'orange', adjust: 'purple', booking: 'cyan', refund: 'red', plan: 'gold', print: 'gray',
};

const BOOKING_STATUS: Record<CardBooking['status'], { label: string; cls: string }> = {
  booked: { label: 'Đã hẹn', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  done: { label: 'Đã làm', cls: 'bg-green-50 text-green-700 border-green-200' },
  cancelled: { label: 'Đã hủy', cls: 'bg-slate-50 text-slate-500 border-slate-200' },
  no_show: { label: 'Không đến', cls: 'bg-red-50 text-red-600 border-red-200' },
};

const ActionBtn = ({ icon, label, onClick, primary, disabled, tip }: { icon: React.ReactNode; label: string; onClick: () => void; primary?: boolean; disabled?: boolean; tip?: string }) => (
  <Tooltip title={tip}>
    <button type="button" onClick={onClick} disabled={disabled}
      className={`flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40 ${primary ? 'bg-[#357f96] text-white hover:bg-[#2c6b7f]' : 'border border-slate-300 bg-white text-slate-700 hover:border-[#3e91ab] hover:text-[#2c6b7f]'}`}>
      {icon}{label}
    </button>
  </Tooltip>
);

export default function CardDetail({ cardId, onClose }: { cardId: string | null; onClose: () => void }) {
  const card = useTreatmentStore(s => s.cards.find(c => c.id === cardId)) || null;
  const cfg = useTreatmentStore(s => s.config);
  const cancelUsage = useTreatmentStore(s => s.cancelUsage);
  const setBookingStatus = useTreatmentStore(s => s.setBookingStatus);
  const approveRefund = useTreatmentStore(s => s.approveRefund);
  const rejectRefund = useTreatmentStore(s => s.rejectRefund);
  const { can, userName } = useTreatmentAuth();
  const pendingReq = useTreatmentStore(s => (cardId ? pendingRequestOf(s.payRequests, cardId) : undefined));
  const [modal, setModal] = useState<ModalKey | null>(null);
  const [booking, setBooking] = useState<CardBooking | null>(null);
  const [cancelling, setCancelling] = useState<{ usage: CardUsage; reason: string } | null>(null);
  const [bookingAction, setBookingAction] = useState<{ b: CardBooking; status: 'cancelled' | 'no_show'; reason: string } | null>(null);
  const [rejecting, setRejecting] = useState<{ id: string; reason: string } | null>(null);
  const [tab, setTab] = useState('lines');

  const months = useMemo(() => (card ? monthlyUsage([card], 12).filter((m, i, arr) => m.visits > 0 || i >= arr.length - 6) : []), [card]);
  if (!card) return <Drawer open={false} onClose={onClose} />;

  const f = cardFlags(card, cfg);
  const bal = cardBalance(card);
  const rows = installmentRows(card, cfg);
  const lines = lineBalances(card);
  const debt = outstanding(card);
  const paid = paidAmount(card);
  const closed = card.status !== 'active';
  const open = (k: ModalKey, b: CardBooking | null = null) => { setBooking(b); setModal(k); };
  const usages = [...card.usages].sort((a, b) => b.at.localeCompare(a.at));
  const bookings = [...card.bookings].sort((a, b) => (a.status === 'booked' ? 0 : 1) - (b.status === 'booked' ? 0 : 1) || a.at.localeCompare(b.at));
  const upcoming = card.bookings.filter(b => b.status === 'booked').length;

  const usagesByMonth = usages.reduce<Record<string, CardUsage[]>>((acc, u) => {
    const k = dayjs(u.at).format('MM/YYYY');
    (acc[k] = acc[k] || []).push(u);
    return acc;
  }, {});

  const tabs = [
    {
      key: 'lines', label: card.kind === 'session' ? 'Dịch vụ trong thẻ' : 'Số dư thẻ',
      children: card.kind === 'session' ? (
        <Table size="small" rowKey="serviceId" pagination={false} dataSource={lines} className="custom-ant-table"
          columns={[
            { title: 'Dịch vụ', render: (_, l) => <span>{l.name}<div className="text-xs text-slate-400">{l.group}</div></span> },
            { title: 'Loại', width: 90, render: (_, l) => <Pill label={ROLE_LABEL[l.role]} cls={l.role === 'bonus' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-50 text-slate-600 border-slate-200'} /> },
            { title: 'Theo gói', width: 80, align: 'right', dataIndex: 'total' },
            { title: 'Điều chỉnh', width: 90, align: 'right', render: (_, l) => (l.bonus ? <span className={l.bonus > 0 ? 'text-green-600' : 'text-red-600'}>{l.bonus > 0 ? '+' : ''}{l.bonus}</span> : '—') },
            { title: 'Đã dùng', width: 80, align: 'right', dataIndex: 'used' },
            { title: 'Hoàn', width: 70, align: 'right', render: (_, l) => l.refunded || '—' },
            { title: 'Đã hẹn', width: 70, align: 'right', render: (_, l) => l.booked || '—' },
            { title: 'Còn lại', width: 80, align: 'right', render: (_, l) => <b className={l.available <= cfg.lowSessions ? 'text-orange-600' : 'text-[#2c6b7f]'}>{Math.max(0, l.available)}</b> },
            { title: 'Giá trị/buổi', width: 110, align: 'right', render: (_, l) => (l.unitValue ? money(l.unitValue) : '—') },
          ]} />
      ) : (
        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <div className="space-y-1 rounded-lg bg-slate-50 p-3">
            <div className="flex justify-between"><span>Mệnh giá</span><b>{money(card.faceValue)}đ</b></div>
            {card.valueAdjust !== 0 && <div className="flex justify-between"><span>Điều chỉnh</span><span>{card.valueAdjust > 0 ? '+' : ''}{money(card.valueAdjust)}đ</span></div>}
            <div className="flex justify-between"><span>Đã dùng</span><span>-{money(bal.usedValue)}đ</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-1 text-base"><span>Số dư</span><b className="text-[#2c6b7f]">{money(bal.remainingValue)}đ</b></div>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <div className="mb-1 font-medium">Dịch vụ áp dụng</div>
            {card.allowedServiceIds.length ? card.allowedServiceIds.map(id => <div key={id}>{serviceOf(id)?.name}</div>) : <div className="text-slate-500">Mọi dịch vụ (trừ theo giá niêm yết)</div>}
          </div>
        </div>
      ),
    },
    {
      key: 'pay', label: <span>Thanh toán {f.overdue && <span className="ml-1 inline-block h-2 w-2 rounded-full bg-red-500" />}</span>,
      children: (
        <div className="space-y-4">
          <Table size="small" rowKey="no" pagination={false} dataSource={rows} className="custom-ant-table"
            columns={[
              { title: 'Đợt', width: 60, dataIndex: 'no' },
              { title: 'Đến hạn', width: 110, render: (_, r) => fmtDate(r.dueDate) },
              { title: 'Phải thu', align: 'right', render: (_, r) => money(r.amount) },
              { title: 'Đã thu', align: 'right', render: (_, r) => money(r.paid) },
              { title: 'Còn thiếu', align: 'right', render: (_, r) => (r.left ? <b className={r.state === 'overdue' ? 'text-red-600' : ''}>{money(r.left)}</b> : '—') },
              { title: 'Trạng thái', width: 170, render: (_, r) => <span className="flex items-center gap-1"><Pill {...INSTALLMENT_STATE[r.state]} />{r.state === 'overdue' && <span className="text-xs text-red-600">{r.overdueDays} ngày</span>}{r.state === 'due_soon' && <span className="text-xs text-amber-700">còn {r.daysLeft} ngày</span>}</span> },
            ]} />
          <div>
            <div className="mb-1 font-semibold text-slate-700">Các lần thanh toán</div>
            {card.payments.length ? (
              <Table size="small" rowKey="id" pagination={false} dataSource={[...card.payments].reverse()} className="custom-ant-table"
                columns={[
                  { title: 'Mã', width: 110, render: (_, p) => <span className="font-mono text-xs">{p.posTxnId || p.id}</span> },
                  { title: 'Thời gian', width: 140, render: (_, p) => fmtDateTime(p.at) },
                  { title: 'Phương thức', render: (_, p) => <span>{METHOD_LABEL[p.method]}{p.txnRef && <span className="ml-1 font-mono text-xs text-slate-400">{p.txnRef}</span>}</span> },
                  { title: 'Thu ngân', render: (_, p) => <span>{p.by}<div className="text-xs text-slate-400">{branchName(p.branchId)}</div></span> },
                  { title: 'Số tiền', align: 'right', render: (_, p) => <b className="text-green-700">{money(p.amount)}</b> },
                  {
                    title: '', width: 60, align: 'right',
                    render: (_, p) => (
                      <Dropdown trigger={['click']} menu={{ items: [{ key: 'K80', label: 'In phiếu thu K80' }, { key: 'A4', label: 'In phiếu thu A4' }], onClick: ({ key }) => printCardReceipt(card, p.id, key as 'K80' | 'A4') }}>
                        <button className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-[#2c6b7f]" aria-label="In phiếu thu"><Printer className="h-4 w-4" /></button>
                      </Dropdown>
                    ),
                  },
                ]} />
            ) : <div className="text-sm text-slate-500">Chưa thu tiền</div>}
          </div>
          {card.refunds.length > 0 && (
            <div>
              <div className="mb-1 font-semibold text-slate-700">Hoàn tiền</div>
              <div className="space-y-2">
                {card.refunds.map(r => (
                  <div key={r.id} className="rounded-lg border border-slate-200 p-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span><b>{r.mode === 'full' ? 'Hoàn toàn bộ' : 'Hoàn một phần'}</b> · <span className="font-mono text-xs">{r.posRefundId || r.id}</span> · {fmtDateTime(r.at)}</span>
                      <Pill {...(r.status === 'paid' ? { label: 'Đã chi', cls: 'bg-green-50 text-green-700 border-green-200' } : r.status === 'pending' ? { label: 'Chờ duyệt', cls: 'bg-amber-50 text-amber-700 border-amber-200' } : { label: 'Từ chối', cls: 'bg-red-50 text-red-600 border-red-200' })} />
                    </div>
                    <div className="mt-1 text-slate-600">
                      {r.sessions.length > 0 && <span>{r.sessions.map(s => `${serviceOf(s.serviceId)?.name} ×${s.qty}`).join(', ')} · </span>}
                      Hoàn <b className="text-violet-700">{money(r.amount)}đ</b>{r.fee > 0 && ` (phí ${money(r.fee)}đ)`} · {METHOD_LABEL[r.method]} · {r.reason}
                    </div>
                    {r.rejectReason && <div className="text-xs text-red-600">Lý do từ chối: {r.rejectReason}</div>}
                    {r.status === 'pending' && (r.posRefundId
                      ? <Link to={`/pos/refunds?id=${r.posRefundId}`} className="mt-1 inline-block text-xs font-semibold text-[#2c6b7f] hover:underline">Duyệt / chi tại màn Hoàn tiền POS →</Link>
                      : can('approve') && (r.by !== userName) && (
                        <div className="mt-2 flex gap-2">
                          <button onClick={() => { approveRefund(card.id, r.id, userName); message.success('Đã duyệt và chi hoàn'); }} className="rounded-lg bg-green-600 px-3 py-1 text-xs font-semibold text-white">Duyệt & chi</button>
                          <button onClick={() => setRejecting({ id: r.id, reason: '' })} className="rounded-lg border border-red-200 px-3 py-1 text-xs font-semibold text-red-600">Từ chối</button>
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'usage', label: `Sử dụng (${card.usages.filter(u => !u.cancelled).length})`,
      children: (
        <div className="space-y-4">
          {months.some(m => m.visits > 0) && (
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={months} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" fontSize={11} />
                  <YAxis allowDecimals={false} fontSize={11} />
                  <RTooltip formatter={(v: number, k: string) => (k === 'value' ? [`${money(v)}đ`, 'Giá trị'] : [v, k === 'sessions' ? 'Buổi' : 'Lượt'])} />
                  <Bar dataKey={card.kind === 'session' ? 'sessions' : 'visits'} fill="#3e91ab" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {Object.keys(usagesByMonth).length === 0 && <Empty description="Chưa có lượt sử dụng" />}
          {Object.entries(usagesByMonth).map(([month, list]) => (
            <div key={month}>
              <div className="mb-1 flex items-center justify-between text-sm font-semibold text-slate-700">
                <span>Tháng {month}</span>
                <span className="text-xs font-normal text-slate-500">
                  {list.filter(u => !u.cancelled).length} lượt · {card.kind === 'session'
                    ? `${list.filter(u => !u.cancelled).reduce((s, u) => s + u.items.reduce((x, i) => x + i.qty, 0), 0)} buổi`
                    : `${money(list.filter(u => !u.cancelled).reduce((s, u) => s + u.items.reduce((x, i) => x + i.value, 0), 0))}đ`}
                </span>
              </div>
              <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {list.map(u => {
                  const cancellable = !u.cancelled && canCancelUsage(u.at, cfg) && (can('delete') || u.by === userName);
                  return (
                    <div key={u.id} className={`flex flex-wrap items-start justify-between gap-2 px-3 py-2 text-sm ${u.cancelled ? 'bg-slate-50 text-slate-400 line-through' : ''}`}>
                      <div className="min-w-0">
                        <div className="font-medium">{u.items.map(i => `${i.name} ×${i.qty}`).join(', ')}{card.kind === 'value' && <span className="ml-1 text-[#2c6b7f]">-{money(u.items.reduce((s, i) => s + i.value, 0))}đ</span>}</div>
                        <div className="text-xs text-slate-500">
                          {fmtDateTime(u.at)} · {branchName(u.branchId)} · KTV {staffName(u.staffId) || u.staffId} · ghi bởi {u.by}
                          {u.signed && ' · khách đã ký'}
                        </div>
                        {u.note && <div className="text-xs text-slate-600">“{u.note}”</div>}
                        {u.overrideBy && <div className="text-xs text-amber-700">Vượt quy tắc – {u.overrideBy} duyệt: {u.overrideReason}</div>}
                        {u.cancelled && <div className="text-xs no-underline">Đã hủy bởi {u.cancelled.by}: {u.cancelled.reason}</div>}
                      </div>
                      {cancellable && <button onClick={() => setCancelling({ usage: u, reason: '' })} className="text-xs font-semibold text-red-600 hover:underline">Hủy lượt</button>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ),
    },
    {
      key: 'bookings', label: `Lịch hẹn${upcoming ? ` (${upcoming})` : ''}`,
      children: bookings.length ? (
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {bookings.map(b => (
            <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
              <div>
                <div className="font-medium">{dayjs(b.at).format('HH:mm · DD/MM/YYYY')} – {b.name}</div>
                <div className="text-xs text-slate-500">{branchName(b.branchId)}{b.staffId && ` · KTV ${staffName(b.staffId)}`} · đặt bởi {b.by}{b.note && ` · ${b.note}`}{b.cancelReason && ` · ${b.cancelReason}`}</div>
              </div>
              <div className="flex items-center gap-2">
                <Pill {...BOOKING_STATUS[b.status]} />
                {b.status === 'booked' && can('create') && <>
                  <button onClick={() => open('deduct', b)} className="rounded-lg bg-[#357f96] px-2.5 py-1 text-xs font-semibold text-white">Check-in</button>
                  <button onClick={() => setBookingAction({ b, status: 'no_show', reason: '' })} className="text-xs text-slate-500 hover:text-red-600">Không đến</button>
                  <button onClick={() => setBookingAction({ b, status: 'cancelled', reason: '' })} className="text-xs text-slate-500 hover:text-red-600">Hủy</button>
                </>}
              </div>
            </div>
          ))}
        </div>
      ) : <Empty description="Chưa có lịch hẹn" />,
    },
    {
      key: 'history', label: 'Lịch sử giao dịch',
      children: (
        <Timeline className="!mt-2" items={[...card.events].reverse().map(e => ({
          color: EVENT_COLOR[e.kind],
          children: (
            <div className="text-sm">
              <div className="flex flex-wrap justify-between gap-2"><span>{e.text}</span>{e.amount !== undefined && <b className={e.amount < 0 ? 'text-violet-700' : 'text-green-700'}>{e.amount > 0 ? '+' : ''}{money(e.amount)}đ</b>}</div>
              <div className="text-xs text-slate-400">{fmtDateTime(e.at)} · {e.by}</div>
            </div>
          ),
        }))} />
      ),
    },
  ];

  return (
    <Drawer open={!!cardId} onClose={onClose} width={900} closable={false} destroyOnHidden
      title={
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-lg font-bold">{card.customerName}</span>
              <span className="text-sm font-normal text-slate-500">{formatPhone(card.customerPhone)}</span>
              <Pill {...CARD_STATUS[f.status]} />
              <FlagPills f={f} />
            </div>
            <div className="text-sm font-normal text-slate-600">
              {card.packageName} · <span className="font-mono">{card.id}</span> · {KIND_LABEL[card.kind]} · {SOURCE_LABEL[card.source]}
              {card.orderId && <> · <Link to={`/pos/orders?id=${card.orderId}&from=2000-01-01`} className="font-mono text-[#2c6b7f] hover:underline">{card.orderId}</Link></>}
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100" aria-label="Đóng"><X className="h-5 w-5" /></button>
        </div>
      }>
      <div className="grid gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-slate-200 p-3 sm:col-span-2"><div className="mb-1 text-xs uppercase text-slate-500">Số dư thẻ</div><BalanceCell card={card} wide /></div>
        <div className="rounded-xl border border-slate-200 p-3">
          <div className="text-xs uppercase text-slate-500">Hạn dùng</div>
          {noExpiry(card.expiresAt) ? <>
            <div className="text-base font-bold">Không thời hạn</div>
            <div className="text-xs text-slate-500">dùng đến khi hết thẻ</div>
          </> : awaitingActivation(card) ? <>
            <div className={`text-base font-bold ${f.status === 'expired' ? 'text-orange-600' : 'text-amber-700'}`}>Chờ kích hoạt</div>
            <div className="text-xs text-slate-500">
              {f.status === 'expired' ? 'quá hạn kích hoạt' : 'dùng buổi đầu'} trước {fmtDate(card.expiresAt)}{f.daysToExpire >= 0 ? ` (còn ${f.daysToExpire} ngày)` : ''}
              {' · '}sau đó hạn {card.validityMonths} tháng
            </div>
          </> : <>
            <div className={`text-base font-bold ${f.status === 'expired' ? 'text-orange-600' : ''}`}>{fmtDate(card.expiresAt)}</div>
            <div className="text-xs text-slate-500">{f.daysToExpire >= 0 ? `còn ${f.daysToExpire} ngày` : `quá ${-f.daysToExpire} ngày`}{card.activatedAt ? ` · kích hoạt ${fmtDate(card.activatedAt)}` : ''}</div>
          </>}
        </div>
        <div className="rounded-xl border border-slate-200 p-3">
          <div className="text-xs uppercase text-slate-500">Đã thu / giá thẻ</div>
          <div className="text-base font-bold">{money(paid)} <span className="text-xs font-normal text-slate-500">/ {money(card.price)}</span></div>
          <div className={`text-xs ${debt ? 'font-semibold text-red-600' : 'text-slate-500'}`}>{debt ? `Còn nợ ${money(debt)}đ` : 'Đã thanh toán đủ'}</div>
        </div>
      </div>
      <div className="mt-2 text-xs text-slate-500">
        Bán {fmtDateTime(card.createdAt)} tại {branchName(card.branchId)} · thu ngân {card.soldBy}{card.consultant && ` · tư vấn ${card.consultant}`} · giá trị đã dùng {money(consumedAmount(card))}đ
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {can('create') && <ActionBtn primary icon={<ScanLine className="h-4 w-4" />} label="Trừ buổi" onClick={() => open('deduct')} disabled={f.status !== 'active'} tip={f.status === 'expired' ? 'Thẻ hết hạn – gia hạn trước' : undefined} />}
        {can('create') && <ActionBtn icon={<CalendarPlus className="h-4 w-4" />} label="Đặt lịch" onClick={() => open('booking')} disabled={f.status !== 'active'} />}
        {can('create') && <ActionBtn icon={<CircleDollarSign className="h-4 w-4" />} label={pendingReq ? 'Chờ POS thu…' : 'Gửi POS thu tiền'} onClick={() => open('pay')} disabled={!debt && !pendingReq} tip={pendingReq ? `Yêu cầu ${pendingReq.id} đang chờ thu ngân xác nhận` : 'Gửi yêu cầu thu tiền sang máy POS – tiền chỉ ghi nhận khi POS xác nhận'} />}
        {can('edit') && <ActionBtn icon={<CalendarClock className="h-4 w-4" />} label="Gia hạn" onClick={() => open('extend')} disabled={closed || noExpiry(card.expiresAt)} tip={noExpiry(card.expiresAt) ? 'Thẻ không thời hạn' : undefined} />}
        {can('edit') && <ActionBtn icon={<SlidersHorizontal className="h-4 w-4" />} label="Điều chỉnh" onClick={() => open('adjust')} disabled={closed} />}
        {can('edit') && <ActionBtn icon={<PencilRuler className="h-4 w-4" />} label="Lịch thanh toán" onClick={() => open('plan')} disabled={closed || !debt} />}
        {can('create') && <ActionBtn icon={<Undo2 className="h-4 w-4" />} label="Hoàn tiền" onClick={() => open('refund')} disabled={closed || f.pendingRefund} tip={f.pendingRefund ? 'Đang có phiếu hoàn chờ duyệt' : undefined} />}
        <ActionBtn icon={<FileText className="h-4 w-4" />} label="In sao kê" onClick={() => printCardStatement(card, cfg)} tip="Sao kê A4 cho khách: số dư, lịch thanh toán, các lần đóng tiền, lịch sử sử dụng" />
        <ActionBtn icon={<ReceiptText className="h-4 w-4" />} label="Lịch sử" onClick={() => setTab('history')} />
      </div>

      <Tabs className="mt-2" activeKey={tab} onChange={setTab} items={tabs} />

      <DeductModal card={modal === 'deduct' ? card : null} booking={booking} onClose={() => setModal(null)} />
      <PayRequestModal card={modal === 'pay' ? card : null} onClose={() => setModal(null)} />
      <BookingModal card={modal === 'booking' ? card : null} onClose={() => setModal(null)} />
      <ExtendModal card={modal === 'extend' ? card : null} onClose={() => setModal(null)} />
      <AdjustModal card={modal === 'adjust' ? card : null} onClose={() => setModal(null)} />
      <RefundModal card={modal === 'refund' ? card : null} onClose={() => setModal(null)} />
      <PlanModal card={modal === 'plan' ? card : null} onClose={() => setModal(null)} />

      <Modal open={!!cancelling} title="Hủy lượt sử dụng" centered okText="Hủy lượt" okButtonProps={{ danger: true, disabled: (cancelling?.reason.trim().length || 0) < 5 }} cancelText="Đóng"
        onCancel={() => setCancelling(null)}
        onOk={() => { if (cancelling) { cancelUsage(card.id, cancelling.usage.id, cancelling.reason.trim(), userName); setCancelling(null); message.success('Đã hủy lượt – buổi được hoàn lại vào thẻ'); } }}>
        <p className="mb-2 text-sm text-slate-600">Buổi đã trừ sẽ được cộng lại vào thẻ. Chỉ hủy được trong {cfg.cancelUsageHours} giờ sau khi trừ.</p>
        <Input.TextArea autoFocus rows={2} maxLength={200} value={cancelling?.reason} placeholder="Lý do (tối thiểu 5 ký tự)" onChange={e => cancelling && setCancelling({ ...cancelling, reason: e.target.value })} />
      </Modal>
      <Modal open={!!bookingAction} title={bookingAction?.status === 'no_show' ? 'Khách không đến' : 'Hủy lịch hẹn'} centered okText="Xác nhận" cancelText="Đóng"
        onCancel={() => setBookingAction(null)}
        onOk={() => { if (bookingAction) { setBookingStatus(card.id, bookingAction.b.id, bookingAction.status, userName, bookingAction.reason.trim()); setBookingAction(null); message.success('Đã cập nhật lịch hẹn'); } }}>
        <Input.TextArea rows={2} maxLength={200} value={bookingAction?.reason} placeholder="Ghi chú (không bắt buộc)" onChange={e => bookingAction && setBookingAction({ ...bookingAction, reason: e.target.value })} />
      </Modal>
      <Modal open={!!rejecting} title="Từ chối phiếu hoàn" centered okText="Từ chối" okButtonProps={{ danger: true, disabled: (rejecting?.reason.trim().length || 0) < 5 }} cancelText="Đóng"
        onCancel={() => setRejecting(null)}
        onOk={() => { if (rejecting) { rejectRefund(card.id, rejecting.id, userName, rejecting.reason.trim()); setRejecting(null); message.success('Đã từ chối – buổi được mở lại'); } }}>
        <Input.TextArea rows={2} maxLength={200} value={rejecting?.reason} placeholder="Lý do (tối thiểu 5 ký tự)" onChange={e => rejecting && setRejecting({ ...rejecting, reason: e.target.value })} />
      </Modal>
    </Drawer>
  );
}
